/**
 * Cloudflare Zaraz Analytics Service
 *
 * Provides a unified interface for interacting with Cloudflare Zaraz
 * for Google Analytics 4 and other analytics tools configured in Zaraz.
 *
 * Zaraz Documentation: https://developers.cloudflare.com/zaraz/
 */

class ZarazService {
  constructor() {
    this.isInitialized = false
    this.queue = []
    this.debugMode = import.meta.env.DEV
  }

  /**
   * Initialize Zaraz service and check availability
   */
  initialize() {
    if (typeof window === 'undefined') {
      return
    }

    // Check if Zaraz is available
    if (window.zaraz) {
      // Verify Zaraz is properly configured before marking as initialized
      this.verifyZarazConfiguration()
    } else {
      // Zaraz might load asynchronously, set up a watcher
      this.watchForZaraz()
    }
  }

  /**
   * Watch for Zaraz to become available
   */
  watchForZaraz() {
    let attempts = 0
    const maxAttempts = 20 // Try for 10 seconds

    const checkInterval = setInterval(() => {
      if (window.zaraz) {
        clearInterval(checkInterval)
        // Verify Zaraz is properly configured before marking as initialized
        this.verifyZarazConfiguration()
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval)

        if (this.debugMode) {
          console.warn('⚠️ Zaraz not available - analytics events will not be tracked')
        }
      }
      attempts++
    }, 500)
  }

  /**
   * Verify Zaraz configuration and handle errors gracefully
   */
  verifyZarazConfiguration() {
    // Add error handler for Zaraz fetch failures
    if (window.zaraz && !window.zaraz._errorHandlerAdded) {
      window.zaraz._errorHandlerAdded = true

      // Override zaraz.track to handle errors gracefully
      const originalTrack = window.zaraz.track
      window.zaraz.track = function (eventName, parameters) {
        try {
          // Call original track method
          return originalTrack.call(window.zaraz, eventName, parameters)
        } catch (error) {
          // Silently catch errors to prevent console pollution
          if (import.meta.env.DEV) {
            console.warn('Zaraz tracking error silenced:', error.message)
          }
        }
      }

      // Test if Zaraz can actually send events
      this.testZarazEndpoint()
    }
  }

  /**
   * Test if Zaraz endpoint is accessible
   */
  async testZarazEndpoint() {
    try {
      // Check if we can successfully use zaraz.track() without actually sending a test event
      if (window.zaraz && typeof window.zaraz.track === 'function') {
        // Zaraz is available and track method exists
        this.isInitialized = true
        this.processQueue()
        if (this.debugMode) {
          // eslint-disable-next-line no-console
          console.log('✅ Zaraz Analytics initialized and ready')
        }
      } else {
        // Zaraz not properly configured
        this.isInitialized = false
        if (this.debugMode) {
          console.warn(
            '⚠️ Zaraz not properly configured. Add tools in Cloudflare Dashboard → Zaraz → Tools.'
          )
        }
      }
    } catch (error) {
      this.isInitialized = false
      if (this.debugMode) {
        console.warn('⚠️ Zaraz configuration test failed. Check Dashboard configuration.')
      }
    }
  }

  /**
   * Process queued events once Zaraz is available
   */
  processQueue() {
    while (this.queue.length > 0) {
      const { method, args } = this.queue.shift()
      this[method](...args)
    }
  }

  /**
   * Queue events if Zaraz is not yet available
   */
  queueEvent(method, args) {
    this.queue.push({ method, args })
  }

  /**
   * Track a custom event
   * @param {string} eventName - The name of the event
   * @param {Object} parameters - Event parameters
   */
  trackEvent(eventName, parameters = {}) {
    if (!this.isInitialized) {
      this.queueEvent('trackEvent', [eventName, parameters])
      return
    }

    try {
      if (window.zaraz && window.zaraz.track) {
        window.zaraz.track(eventName, parameters)

        if (this.debugMode) {
          // eslint-disable-next-line no-console
          console.log('📊 Zaraz Event:', eventName, parameters)
        }
      }
    } catch (error) {
      // Silently handle errors to prevent console pollution
      if (this.debugMode) {
        console.warn('Zaraz tracking error:', error.message)
      }
    }
  }

  /**
   * Track a page view (for SPA route changes)
   * Zaraz usually handles this automatically, but this method allows manual control
   */
  trackPageView(path, title) {
    if (!this.isInitialized) {
      this.queueEvent('trackPageView', [path, title])
      return
    }

    try {
      // Zaraz automatically tracks SPA page views
      // This is for manual override if needed
      window.zaraz.spaPageview()

      if (this.debugMode) {
        // eslint-disable-next-line no-console
        console.log('📄 Zaraz Page View:', path, title)
      }
    } catch (error) {
      console.error('Failed to track page view:', error)
    }
  }

  /**
   * Set user properties
   * @param {string} key - Property name
   * @param {any} value - Property value
   */
  setUserProperty(key, value) {
    if (!this.isInitialized) {
      this.queueEvent('setUserProperty', [key, value])
      return
    }

    try {
      // For user properties, prefix with "up." in Zaraz
      window.zaraz.set(`up.${key}`, value)

      if (this.debugMode) {
        // eslint-disable-next-line no-console
        console.log('👤 Zaraz User Property:', key, value)
      }
    } catch (error) {
      console.error('Failed to set user property:', error)
    }
  }

  /**
   * Set a persistent property for all events
   * @param {string} key - Property name
   * @param {any} value - Property value
   */
  setProperty(key, value) {
    if (!this.isInitialized) {
      this.queueEvent('setProperty', [key, value])
      return
    }

    try {
      window.zaraz.set(key, value)

      if (this.debugMode) {
        // eslint-disable-next-line no-console
        console.log('🔧 Zaraz Property:', key, value)
      }
    } catch (error) {
      console.error('Failed to set property:', error)
    }
  }

  /**
   * Track e-commerce events
   * @param {string} eventName - E-commerce event name
   * @param {Object} data - E-commerce data
   */
  trackEcommerce(eventName, data) {
    if (!this.isInitialized) {
      this.queueEvent('trackEcommerce', [eventName, data])
      return
    }

    try {
      window.zaraz.ecommerce(eventName, data)

      if (this.debugMode) {
        // eslint-disable-next-line no-console
        console.log('🛍️ Zaraz E-commerce:', eventName, data)
      }
    } catch (error) {
      console.error('Failed to track e-commerce event:', error)
    }
  }

  /**
   * Track user authentication events
   * @param {string} method - Authentication method (email, google, facebook)
   * @param {string} userId - User identifier
   */
  trackAuth(method, userId) {
    this.trackEvent('login', {
      method,
      user_id: userId,
    })

    // Set user ID for future events
    this.setUserProperty('user_id', userId)
  }

  /**
   * Track user sign out
   */
  trackSignOut() {
    this.trackEvent('logout')

    // Clear user properties
    this.setUserProperty('user_id', null)
  }

  /**
   * Track errors
   * @param {string} description - Error description
   * @param {boolean} fatal - Whether the error is fatal
   */
  trackError(description, fatal = false) {
    this.trackEvent('exception', {
      description,
      fatal,
    })
  }

  /**
   * Track search/chat interactions
   * @param {string} query - Search query or chat message
   * @param {string} type - Type of interaction (search, chat)
   */
  trackSearch(query, type = 'search') {
    // Use different event names for different interaction types
    if (type === 'chat') {
      this.trackEvent('chat_message', {
        message_text: query,
        interaction_type: type,
      })
    } else {
      this.trackEvent('search', {
        search_term: query,
        search_type: type,
      })
    }
  }

  /**
   * Track user engagement
   * @param {string} category - Engagement category
   * @param {string} action - Action taken
   * @param {string} label - Optional label
   * @param {number} value - Optional numeric value
   */
  trackEngagement(category, action, label, value) {
    const parameters = {
      event_category: category,
      event_action: action,
    }

    if (label) parameters.event_label = label
    if (value !== undefined) parameters.value = value

    this.trackEvent('engagement', parameters)
  }
}

// Create singleton instance
const zarazService = new ZarazService()

// Auto-initialize when the module loads
if (typeof window !== 'undefined') {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => zarazService.initialize())
  } else {
    zarazService.initialize()
  }
}

export default zarazService
